import { Button } from '@/components/base/button'
import { ScrollArea } from '@/components/base/scroll-area'
import { ButtonSortAlpha } from '@/components/ButtonSortAlpha'
import { Icon } from '@/components/common/icons/Icon'
import { Contact } from '@/components/Contact'
import { RecentContacts } from '@/components/RecentContacts'
import { Search } from '@/components/Search'
import {
  UnorderedList,
  UnorderedListBottom,
  UnorderedListItem,
} from '@/components/UnorderedList'
import { DEFAULT_CONTEXT, SORT_ORDER } from '@/helpers/constants'
import type { Resource } from '@/helpers/types'
import { cn, sortBy, startsWithPSTN, startsWithSIP } from '@/helpers/utils'
import { useSimpleSearch } from '@/hooks/useSimpleSearch'
import { useSortOrder } from '@/hooks/useSortOrder'
import { useSubscribeConversations } from '@/hooks/useSubscribeConversations'
import { logger } from '@/logger/createLogger'
import { useAuthStore } from '@/stores/auth'
import { useResourcesStore, useResourcesStoreActions } from '@/stores/resources'
import { useUiStoreActions } from '@/stores/ui'
import { useMemo, useState } from 'react'

// TODO: should this logic be consolidated with the one in `CallsView` to dial a PSTN/SIP number?
const navigateToRoomWithSearch = (searchTerm: string) => {
  const isKnownPrefix = (searchTerm: string) =>
    startsWithPSTN(searchTerm) || startsWithSIP(searchTerm)

  // TODO: should SIP/PSTN be handled with the `/private` context always?
  const searchTermToDestinationUri = (searchTerm: string) => {
    if (isKnownPrefix(searchTerm)) {
      return `/${DEFAULT_CONTEXT}/${searchTerm}`
    } else {
      return searchTerm
    }
  }

  const toURI = searchTermToDestinationUri(searchTerm)
  if (!toURI) {
    logger.error(
      `navigateToSearch: No URI to navigate to for search term "${searchTerm}"`,
    )
  }

  const router = useAuthStore.getState().router
  void router?.navigate({ to: `/room/${toURI}` })
}

export const DirectoryView = () => {
  const [selectedResourceId, setSelectedResourceId] = useState('')

  const resourcesByIdMap = useResourcesStore(state => state.resourcesByIdMap)

  const { showContact } = useUiStoreActions()
  const { getResourcesFromMap } = useResourcesStoreActions()

  const { sortOrder, toggleSortOrder } = useSortOrder()

  // TODO: remove when subscription is implemented at top level of the app
  useSubscribeConversations()

  const sortedResources = useMemo(() => {
    const resources = getResourcesFromMap(resourcesByIdMap)
    return sortBy(resources, sortOrder, 'displayName')
  }, [resourcesByIdMap, getResourcesFromMap, sortOrder])

  const { searchResults, handleSearch, handleSearchReset, searchTerm } =
    useSimpleSearch({
      items: sortedResources,
      key: 'displayName',
    })

  const resetContactId = () => {
    setSelectedResourceId('')
  }

  const handleClickResource = (resource: Resource) => {
    setSelectedResourceId(resource.id)
    showContact()
  }

  return (
    <div className="flex h-full">
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-1 pb-3.5 pl-6 pr-5 pt-2">
          <div className="flex items-center justify-between">
            <h2 className="text-4xl font-semibold leading-normal">Directory</h2>
            <ButtonSortAlpha
              disabled={false} // TODO: disable when fetching resources
              isAsc={sortOrder === SORT_ORDER.ASC}
              onClick={toggleSortOrder}
            />
          </div>
          <Search
            formDescription="Search for a directory item."
            onChange={handleSearch}
            onReset={handleSearchReset}
            onSubmit={handleSearch}
            placeholder="Search Directory"
          />
        </div>

        <section className="flex h-full overflow-y-hidden">
          <ScrollArea>
            <UnorderedList className="w-80" aria-label="Directory Resources">
              {searchResults.map(resource => (
                <UnorderedListItem
                  className={cn(
                    'relative flex min-h-10 before:absolute before:inset-0 before:w-2 before:transition-colors hover:bg-neutral-light hover:text-primary-light',
                    selectedResourceId === resource.id
                      ? 'before:bg-primary-light'
                      : 'before:bg-transparent',
                  )}
                  key={resource.id}
                >
                  <p className="line-clamp-2 flex-grow">
                    {resource.displayName}
                  </p>
                  {/* TODO: click to call the resource with the correct video/audio channel */}
                  <Button
                    className="shrink p-0"
                    onClick={() => handleClickResource(resource)}
                    variant="icon"
                  >
                    <Icon
                      tag="keyboard-right-arrow"
                      size="lg"
                      variant="foreground"
                    />
                    <span className="sr-only">Show Resource</span>
                  </Button>
                </UnorderedListItem>
              ))}
              {searchTerm && !searchResults.length && (
                <div className="flex basis-full flex-col items-center justify-center">
                  <div className="flex items-center gap-x-2">
                    <Icon tag="warning" />
                    <h3 className="text-xl font-semibold">Address not found</h3>
                  </div>
                  <p className="flex flex-col items-center p-5 font-montserrat text-sm">
                    <span>No results found for</span>
                    <span className="py-2 font-semibold italic">
                      {' '}
                      {searchTerm}
                    </span>
                    <span> in the directory list.</span>
                    <span className="pt-5">Do you want to dial anyway?</span>
                  </p>
                  <div className="flex gap-x-4">
                    <div className="pb-5">
                      <Button
                        variant="gradient"
                        className="shrink"
                        onClick={() =>
                          navigateToRoomWithSearch(
                            `${searchTerm}?channel=audio`,
                          )
                        }
                      >
                        Audio
                      </Button>
                    </div>
                    {!startsWithPSTN(searchTerm) && (
                      <div className="pb-5">
                        <Button
                          variant="gradient"
                          className="shrink"
                          onClick={() =>
                            navigateToRoomWithSearch(
                              `${searchTerm}?channel=video`,
                            )
                          }
                        >
                          Video
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <UnorderedListBottom>End of List</UnorderedListBottom>
            </UnorderedList>
          </ScrollArea>
        </section>
      </div>
      <div className="flex basis-full flex-col items-center justify-center bg-neutral-light">
        {selectedResourceId ? (
          <>
            {/* TODO: use router to update the path or param based on selected contact? */}
            <Contact
              onClose={resetContactId}
              redirect="/directory"
              resourceId={selectedResourceId}
            />
            <RecentContacts resourceId={selectedResourceId} />
          </>
        ) : (
          <p className="text-center text-sm font-normal text-secondary">
            Select an entry
          </p>
        )}
      </div>
    </div>
  )
}
